import React, {
  ChangeEvent,
  KeyboardEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useFunctionsGatsby } from "./useFunctionsGatsby";
import { useReviewContext } from "./useReviewSnippet";
import { type ReviewsResponse } from "~/api/reviews";
import { type QuestionsResponse } from "~/api/questions";
import { TabbedSectionProps } from "@bared/ui";

export const QUESTIONS_PER_PAGE = 10;

type FitFeedbackStatisticProps = {
  id: number;
  rangeOptions: string[];
  title: string;
  averageRating: string;
};

export function useReviews({
  productGroup,
  sku,
  reviewSize = 10,
  isReviewsSheetOpen = false,
  initialReviewFilter = 0,
}: {
  productGroup: string;
  sku: string;
  reviewSize?: number;
  isReviewsSheetOpen?: boolean;
  initialReviewFilter?: number;
}) {
  const { productReviews, productQuestions, loading, errors } =
    useFunctionsGatsby();
  const { addProductToQueue, setTotalReviewsCount } = useReviewContext();

  const reviewsSortOptions = [
    {
      value: "highest_rating",
      label: "Highest Rating",
    },
    {
      value: "lowest_rating",
      label: "Lowest Rating",
    },
    {
      value: "date_desc",
      label: "Most Recent",
    },
  ];
  const questionsSortOptions = [
    {
      value: "date_desc",
      label: "Most Recent",
    },
    {
      value: "date_asc",
      label: "Oldest",
    },
  ];

  const [page, setPage] = useState<number>(1);
  const [reviewFilter, setReviewFilter] = useState<number>(initialReviewFilter);
  const [average, setAverage] = useState<number>(0);
  const [starDistribution, setStarDistribution] = useState<
    Record<number, number>
  >({});
  const [fitFeedbackStatistic, setFitFeedbackStatistic] = useState<
    FitFeedbackStatisticProps[]
  >([]);

  const [reviews, setReviews] = useState<ReviewsResponse[]>([]);
  const [reviewsSortValue, setReviewsSortValue] = useState<string>(
    reviewsSortOptions[0].value,
  );
  const [questionsSortValue, setQuestionsSortValue] = useState<string>(
    questionsSortOptions[0].value,
  );
  const [searchKeyword, setSearchKeyword] = useState<string | undefined>(
    undefined,
  );
  const [total, setTotal] = useState<number>(0);
  const [questions, setQuestions] = useState<QuestionsResponse[]>([]);
  const [totalQuestions, setTotalQuestions] = useState<number>(0);
  const [questionPageCursor, setQuestionPageCursor] = useState<number>(1);
  const [totalPageCount, setTotalPageCount] = useState<number>(0);
  const [totalQuestionPageCount, setTotalQuestionPageCount] =
    useState<number>(0);
  const [totalResultsCount, setTotalResultsCount] = useState<number>(0);
  const [reviewsPerPage, setReviewsPerPage] = useState<number>(0);
  const [questionsPerPage, setQuestionsPerPage] = useState<number>(0);

  const mapStarDistribution = (ratings: Record<string, number>) => {
    return Array.from({ length: 5 })
      .map((_val, index) => 5 - index)
      .reduce((prev, curr) => {
        const buckets = Object.keys(ratings)?.filter(
          (key) => Number(key) === curr,
        );

        return {
          ...prev,
          [curr]: ratings[Number(buckets.toString())] || 0,
        };
      }, {});
  };

  const getReviews = useCallback(
    async (
      page: number,
      reviewFilter: number,
      sortOrder: string,
      keyword?: string,
    ) => {
      const { status, body } = await productReviews(
        productGroup,
        page,
        reviewSize,
        reviewFilter,
        sortOrder,
        keyword,
      );

      if (status !== "success") {
        return;
      }

      const {
        reviews: data,
        reviewCount,
        resultsCount,
        stats: { attributes, ratings },
        averageRating,
      } = body;

      const fitFeedbackStats: FitFeedbackStatisticProps[] = attributes.map(
        (attribute) => {
          return {
            id: attribute.id,
            rangeOptions: attribute.options,
            title: attribute.question,
            averageRating: attribute.average_rating,
          };
        },
      );

      // Checks if the next pagination arrow is disabled. If it is that is the last page
      const isLastPage =
        document.querySelector('[rel="next"]')?.ariaDisabled === "true";
      if (isLastPage) {
        setReviewsPerPage(resultsCount);
      } else {
        setReviewsPerPage(page * data.length);
      }

      if (!reviewFilter) {
        setStarDistribution(mapStarDistribution(ratings));
      }

      setAverage(parseFloat(averageRating));
      setReviews(data);
      setTotal(reviewCount);
      setTotalResultsCount(resultsCount);
      setTotalPageCount(Math.ceil(resultsCount / reviewSize));
      setFitFeedbackStatistic(fitFeedbackStats);

      // Set the reviews count in the context for use in ReviewNugget
      if (setTotalReviewsCount) {
        setTotalReviewsCount(reviewCount);
      }
    },
    [productReviews, productGroup, setTotalReviewsCount],
  );

  const getQuestions = useCallback(
    async (page: number, sortOrder: string) => {
      const { status, body } = await productQuestions(
        productGroup,
        page,
        QUESTIONS_PER_PAGE,
        sortOrder,
      );

      if (status !== "success") {
        return;
      }

      const { questions: data, questionsCount } = body;

      // Checks if the next pagination arrow is disabled. If it is that is the last page
      const isLastPage =
        document.querySelector('[rel="next"]')?.ariaDisabled === "true";
      if (isLastPage) {
        setQuestionsPerPage(questionsCount);
      } else {
        setQuestionsPerPage(page * data.length);
      }

      setQuestions(data);
      setTotalQuestions(questionsCount);
      setTotalQuestionPageCount(Math.ceil(questionsCount / QUESTIONS_PER_PAGE));
    },
    [productQuestions, productGroup],
  );

  useEffect(() => {
    if (!isReviewsSheetOpen) {
      return;
    }

    getReviews(page, reviewFilter, reviewsSortValue, searchKeyword);
  }, [page, reviewFilter, reviewsSortValue, searchKeyword, isReviewsSheetOpen]);

  useEffect(() => {
    if (!isReviewsSheetOpen) {
      return;
    }

    getQuestions(questionPageCursor, questionsSortValue);
  }, [questionPageCursor, questionsSortValue, isReviewsSheetOpen]);

  // Update filter when sheet opens with initial filter
  useEffect(() => {
    if (isReviewsSheetOpen) {
      setReviewFilter(initialReviewFilter);
    }
  }, [isReviewsSheetOpen, initialReviewFilter]);

  const handleLoadMoreReviews = (event: { selected: number }) => {
    setPage(event.selected + 1);

    const element = document.getElementById("reviews-questions-container");

    if (!loading && element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  };

  const handleLoadMoreQuestions = (event: { selected: number }) => {
    setQuestionPageCursor(event.selected + 1);

    const element = document.getElementById("reviews-questions-container");

    if (!loading && element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  };

  const handleTabChange = () => {
    setPage(1);
    setQuestionPageCursor(1);

    const element = document.getElementById("reviews-questions-container");

    if (!loading && element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  };

  const handleSearchIconClick = () => {
    const searchInput = document.getElementById("search-keyword")?.value;
    setPage(1);
    setReviews([]);
    setSearchKeyword(searchInput);
  };

  const handleSearch = (e: KeyboardEventHandler<HTMLInputElement>) => {
    if (e.code === "Enter") {
      setPage(1);
      setReviews([]);
      setSearchKeyword(e.target.value);
    }
  };

  const handleReviewsSortOptionChange = ({
    target: { value },
  }: ChangeEvent<HTMLSelectElement>) => {
    setReviewsSortValue(value);
    setReviewFilter(0);
  };

  const handleQuestionsSortOptionChange = ({
    target: { value },
  }: ChangeEvent<HTMLSelectElement>) => {
    setQuestionsSortValue(value);
  };

  const handleClickReviewFilter = (filter: number) => {
    setPage(1);
    setReviews([]);
    setReviewFilter(filter);
  };

  const handleClearReviewFilter = () => {
    setPage(1);
    setReviews([]);
    setReviewFilter(0);
  };

  useEffect(() => {
    if (!sku || !addProductToQueue) {
      return;
    }

    addProductToQueue(sku);
  }, [sku, addProductToQueue]);

  const tabs: TabbedSectionProps["tabs"] = useMemo(
    () => [
      { title: `All Reviews (${total})`, handle: "reviews" },
      { title: `All Questions (${totalQuestions})`, handle: "questions" },
    ],
    [total, totalQuestions],
  );

  return {
    averageScore: average,
    reviews,
    questions,
    starDistribution,
    totalQuestions,
    totalQuestionPageCount,
    totalReviews: total,
    totalPageCount,
    totalResultsCount,
    fitFeedbackStatistic,
    reviewsSortOptions,
    questionsSortOptions,
    reviewsPerPage,
    questionsPerPage,
    loading,
    reviewFilter,
    tabs,
    page,
    questionPageCursor,
    handleClearReviewFilter,
    handleClickReviewFilter,
    handleLoadMoreQuestions,
    handleLoadMoreReviews,
    handleQuestionsSortOptionChange,
    handleReviewsSortOptionChange,
    handleSearch,
    handleSearchIconClick,
    handleTabChange,
  };
}
